import { supabase } from '@/lib/supabase';

// Supabase Storage utility for UGC recordings
// Bucket expected: "ugc-recordings" (create in Supabase Storage, private with RLS)
export const storageService = {
  bucket: 'ugc-recordings',

  /**
   * Upload a UGC recording (Blob/File) to Supabase Storage.
   * Returns storage path and URL (signed) for access.
   * @param {Blob|File} fileBlob
   * @param {string} userId
   * @param {string} filename
   */
  async uploadUGCRecording(fileBlob, userId, filename = `recording-${Date.now()}.webm`) {
    try {
      if (!fileBlob) throw new Error('Missing file blob');
      if (!userId) throw new Error('Missing userId');

      // Verify bucket exists before attempting upload
      const bucketOk = await this.ensureBucketExists();
      if (!bucketOk) {
        const err = new Error('Storage bucket "ugc-recordings" not found. Create it in Supabase Storage (private) and apply storage RLS.');
        err.code = 'BUCKET_NOT_FOUND';
        throw err;
      }

      const safeUser = String(userId);
      const path = `${safeUser}/${Date.now()}-${filename}`;
      const contentType = fileBlob?.type || 'video/webm';

      const { data, error } = await supabase.storage
        .from(this.bucket)
        .upload(path, fileBlob, { contentType, upsert: false });

      if (error) throw error;

      // Always prefer a signed URL for private bucket access
      const signedUrl = await this.createSignedUrl(path, 3600);
      const publicUrl = this.getPublicUrl(path);

      return {
        path,
        publicUrl,
        signedUrl,
        url: signedUrl?.signedUrl || publicUrl || null
      };
    } catch (err) {
      console.error('[StorageService] Upload failed:', err?.message || err);
      throw err;
    }
  },

  /**
   * Get a public URL for a storage path (returns null if not available)
   */
  getPublicUrl(path) {
    try {
      const { data } = supabase.storage.from(this.bucket).getPublicUrl(path);
      return data?.publicUrl || null;
    } catch {
      return null;
    }
  },

  /**
   * Create a signed URL for a storage path
   */
  async createSignedUrl(path, expiresIn = 3600) {
    try {
      const { data, error } = await supabase.storage
        .from(this.bucket)
        .createSignedUrl(path, expiresIn);
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('[StorageService] Signed URL error:', err?.message || err);
      return null;
    }
  },

  /**
   * List user's recordings (prefix = userId/)
   */
  async listUserRecordings(userId) {
    try {
      const prefix = `${userId}/`;
      const { data, error } = await supabase.storage
        .from(this.bucket)
        .list(prefix, { limit: 1000 });
      if (error) throw error;
      // Return full paths relative to bucket
      return (data || []).map((obj) => `${prefix}${obj.name}`);
    } catch (err) {
      console.error('[StorageService] List user recordings error:', err?.message || err);
      return [];
    }
  },

  /**
   * Delete a single recording path
   */
  async deleteUGCRecording(path) {
    try {
      const { error } = await supabase.storage.from(this.bucket).remove([path]);
      if (error) throw error;
      return true;
    } catch (err) {
      console.error('[StorageService] Delete recording error:', err?.message || err);
      return false;
    }
  },

  /**
   * Delete all recordings for a user
   */
  async deleteUserRecordings(userId) {
    try {
      const paths = await this.listUserRecordings(userId);
      if (paths.length === 0) return 0;
      const chunks = [];
      for (let i = 0; i < paths.length; i += 1000) chunks.push(paths.slice(i, i + 1000));
      let removed = 0;
      for (const batch of chunks) {
        const { data, error } = await supabase.storage.from(this.bucket).remove(batch);
        if (error) throw error;
        removed += data?.length || batch.length;
      }
      return removed;
    } catch (err) {
      console.error('[StorageService] Delete user recordings error:', err?.message || err);
      return 0;
    }
  }
  ,

  /**
   * Check if the configured bucket exists
   */
  async ensureBucketExists() {
    try {
      const { data, error } = await supabase.storage
        .from(this.bucket)
        .list('', { limit: 1 });
      if (error) {
        // Supabase returns StorageApiError with message "Bucket not found"
        if (String(error?.message || '').toLowerCase().includes('bucket not found')) return false;
        throw error;
      }
      return true;
    } catch (err) {
      // Treat unexpected errors as missing bucket to show actionable UX
      console.error('[StorageService] ensureBucketExists error:', err?.message || err);
      return false;
    }
  }
};

export default storageService;
